/**
 * Lambda Function URL (see amplify/backend.ts) returns the latest S3 object key
 * under public/therapist-profiles/{userId}/ when GraphQL has no profilePhotoKey yet.
 */

type AmplifyOutputsCustom = {
  custom?: { therapistProfilePhotoResolverUrl?: string };
};

let resolverUrlCache: string | null | undefined;

export async function getTherapistProfilePhotoResolverUrl(): Promise<string | null> {
  if (resolverUrlCache !== undefined) return resolverUrlCache;
  // Prefer recreated Function URL (old amplify_outputs URL returns 403).
  const MANUAL_FALLBACK =
    'https://7j3oczbimxg4vfao6jvvm6mdxm0stnly.lambda-url.us-east-2.on.aws/';
  resolverUrlCache = MANUAL_FALLBACK;
  return resolverUrlCache;
}

const BATCH_SIZE = 40;

/**
 * Batch-resolve storage keys for many therapist user ids (POST to resolver).
 */
export async function fetchTherapistProfilePhotoKeysByUserIds(
  userIds: string[],
): Promise<Record<string, string>> {
  const base = await getTherapistProfilePhotoResolverUrl();
  if (!base || userIds.length === 0) return {};

  const unique = [...new Set(userIds.map((id) => id.trim()).filter(Boolean))];
  const out: Record<string, string> = {};
  const endpoint = base.replace(/\/$/, '');

  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const chunk = unique.slice(i, i + BATCH_SIZE);
    try {
      // text/plain avoids CORS preflight — Function URL Allow-Headers is empty.
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ userIds: chunk }),
      });
      if (!res.ok) continue;
      const data = (await res.json()) as { keys?: Record<string, string | null | undefined> };
      const keys = data.keys ?? {};
      for (const [uid, key] of Object.entries(keys)) {
        const k = key?.trim();
        if (k) out[uid] = k;
      }
    } catch {
      /* resolver optional */
    }
  }
  return out;
}
